import moment from "moment";
import { createPinia } from "pinia";
import { v4 as uuidv4 } from "uuid";
import { createApp } from "vue";
import App from "./App.vue";
import { useAnalytics } from "./composables/useAnalytics";
import { installCordovaBridge } from "./composables/useCordovaBridge";
import { initFirebaseMessaging } from "./composables/useFirebaseMessaging";
import { i18n } from "./plugins/i18n";
import { registerPrimeVue } from "./plugins/primevue";
import { router } from "./router";
import { useWorktimeStore } from "./stores/worktime";
import "./styles/main.css";
import { isAppRuntime } from "./lib/device";

const mountRoot = document.querySelector("#app");

if (!(mountRoot instanceof HTMLElement)) {
  console.error("No se encontró el contenedor de montaje Vue");
  throw new Error("Vue mount root not found");
}

window.__wktInitialLocationHash = window.location.hash || "";
window.$vueWrapper = mountRoot;
window.$vueWrapperParent = mountRoot.parentElement ?? mountRoot;

mountRoot.setAttribute("aria-hidden", "false");
mountRoot.classList.add("v-application");
document.documentElement.classList.add("v-application");
document.body.classList.add("v-application");
mountRoot.style.display = "block";
mountRoot.style.position = "relative";
mountRoot.style.width = "100%";
mountRoot.style.minHeight = "100%";

function registerPwaServiceWorker(): void {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    isAppRuntime()
  ) {
    return;
  }

  const isSecureContextLike =
    window.isSecureContext ||
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  if (!isSecureContextLike) {
    return;
  }

  const serviceWorkerUrl = `${import.meta.env.BASE_URL}firebase-messaging-sw.js`;
  const serviceWorkerScope = import.meta.env.BASE_URL || "/";

  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register(serviceWorkerUrl, { scope: serviceWorkerScope })
      .catch((error) => {
        console.warn("Unable to register PWA service worker", error);
      });
  });
}

installCordovaBridge();
initFirebaseMessaging();
registerPwaServiceWorker();
window.kc_prevent_preliminary_auth = false;

if (
  typeof window.deviceCordovaWrapperVersion === "string" &&
  window.deviceCordovaWrapperVersion.trim().length > 0
) {
  window.sendCordovaWrapperMessage?.("KC_INIT");
}

const pinia = createPinia();
const app = createApp(App);

app.use(pinia);
app.use(router);
app.use(i18n);
registerPrimeVue(app);
useAnalytics().setupRouterTracking(router);

app.mount(mountRoot);

window.$vue = app;
window.$store = pinia;
window.$i18n = i18n;

if (!window.localStorage.getItem("worktime-device-uuid")) {
  const uuid = uuidv4().toString().toUpperCase().substring(4, 18);
  window.localStorage.setItem("worktime-device-uuid", uuid);
}

const worktimeStore = useWorktimeStore();
const deviceUuid = window.localStorage.getItem("worktime-device-uuid");
if (deviceUuid) {
  worktimeStore.setUuid(deviceUuid);
}

const navigatorLocale = navigator.language || "en";
moment.locale(navigatorLocale);

const isProduction = import.meta.env.PROD;

window.$log = {
  debug: (...args: unknown[]) => {
    if (!isProduction) {
      console.log("[DEBUG]", ...args);
    }
  },
  info: (...args: unknown[]) => {
    console.info("[INFO]", ...args);
  },
  warn: (...args: unknown[]) => {
    console.warn("[WARN]", ...args);
  },
  error: (...args: unknown[]) => {
    console.error("[ERROR]", ...args);
  },
};

window.$log.debug("✅ Worktime Client Vue 3 inicializado correctamente");
window.$log.debug("📦 Runtime root: standalone");
window.$log.debug(`🌐 Locale: ${navigatorLocale}`);
window.$log.debug(`🆔 Device UUID: ${deviceUuid}`);
window.$log.debug("🛠️ Modo:", isProduction ? "Producción" : "Desarrollo");
